"use client";
import React, { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import Link from "next/link";
import { menuData } from "@/data/MenuData";
import Container from "../wrapper/Container";
import InfiniteMarquee from "../animations/InfiniteMarquee";
import { marqueeImages } from "@/data/FeatureGalleryData";

const Footer = () => {
  const footerRef = useRef(null);
  //tracking scroll progress
  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  });
  // smoothness popup
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 25,
    restDelta: 0.001,
  });
  //mask size calculation
  const circlePath = useTransform(
    smoothProgress,
    [0, 0.15, 1],
    [
      "circle(25px at 50% 70px)",
      "circle(45px at 50% 70px)",
      "circle(150% at 50% 50%)",
    ],
  );

  return (
    <footer
      ref={footerRef}
      className="relative w-full h-auto overflow-hidden bg-white"
    >
      <motion.div
        style={{ clipPath: circlePath }}
        className="relative w-full h-full flex flex-col origin-bottom bg-primary text-[#617700] pt-16 pb-8 md:pb-12 items-center overflow-hidden font-medium text-lg"
      >
        <Container className="flex flex-col space-y-12 pt-8">
          {/* ===== Top Section: Navigation Links ===== */}
          <div className="grid grid-cols-2 md:grid-cols-3 w-full gap-y-12 px-4 md:px-10">
            {/* 1. Menu Column (Left) */}
            <div className="flex flex-col gap-1 text-left">
              <h3 className="mb-1">Menu</h3>
              {menuData.footerMenu.map((item, i) => (
                <Link
                  key={i}
                  href={item.href}
                  className="font-semibold text-xl hover:opacity-70 transition-opacity text-[#2F3900]"
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* 2. Socials Column */}
            <div className="flex flex-col gap-1 text-right md:col-start-3">
              <h3 className="mb-1">Socials</h3>
              {menuData.socials.map((item, i) => (
                <a
                  key={i}
                  target="_blank"
                  href={item.href}
                  className="font-semibold text-xl hover:opacity-70 transition-opacity text-[#2F3900]"
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* 3. Contacts Column */}
            <div className="col-span-2 md:col-span-1 md:row-start-1 md:col-start-2 flex flex-col gap-1 items-center text-center">
              <h3 className="mb-1">Contacts</h3>
              {menuData.contacts.map((item, i) => (
                <Link
                  key={i}
                  href={item.href}
                  className="font-semibold text-xl hover:opacity-70 transition-opacity text-[#2F3900]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* ===== Giant Marquee Section ===== */}
          <div className="w-full flex justify-center">
            <InfiniteMarquee
              items={marqueeImages}
              direction="left"
              speed={15}
              isImageFill={false}
              imageClassName="h-17.5 md:h-37.5 lg:h-50  w-auto opacity-95"
              innerClassName="object-contain mix-blend-multiply w-auto h-full"
              // Tile height is 70 / 150 / 200 px and the logos are contained in
              // it, so the widest they get is roughly twice that.
              sizes="(max-width: 768px) 150px, (max-width: 1024px) 300px, 400px"
              gap="gap-8 lg:gap-16"
            />
          </div>

          {/* ===== Bottom Privacy Section ===== */}
          <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:gap-y-8 gap-y-4 text-sm font-medium text-[#2F3900] px-4 md:px-10">
            <div className="col-span-2 md:col-span-1 order-last md:order-0 flex justify-center md:justify-start">
              <p>Copyright 2026</p>
            </div>

            <div className="col-span-1 flex justify-start md:justify-center">
              <Link href="/privacy-policy">Privacy Policy</Link>
            </div>

            <div className="col-span-1 flex justify-end">
              <Link href="/terms-of-use">Terms of Use</Link>
            </div>
          </div>
        </Container>
      </motion.div>
    </footer>
  );
};

export default Footer;
